import type { SkillKey } from "./types";

/** One finished session, as the stats API returns it. */
export interface SessionPoint {
  /** Ended at, ms. */
  t: number;
  n: number;
  c: number;
  points: number;
  /** Sum of capped time ratios, for pace. */
  r: number;
  /** Per skill: [answered, correct, ratio sum]. */
  sk: Partial<Record<SkillKey, [number, number, number]>>;
  /** Ratings at the end of the session. Null for daily challenges (fixed difficulty, no rating change). */
  lv: Partial<Record<SkillKey, number>> | null;
  mode: string;
}

export type Period = "session" | "day" | "week" | "month";
export const PERIOD_LIMIT: Record<Period, number> = { session: 30, day: 30, week: 26, month: 24 };

export interface Bucket {
  key: string; t0: number; sessions: number;
  n: number; c: number; points: number;
  acc: number | null; pace: number | null; level: number | null;
}

const mondayOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate() - ((d.getDay() + 6) % 7));

function keyFor(t: number, period: Period, i: number): string {
  const d = new Date(t);
  if (period === "session") return "s" + i;
  if (period === "day") return d.toDateString();
  if (period === "week") return mondayOf(d).toDateString();
  return d.getFullYear() + "-" + d.getMonth();
}

/** Group sessions into chartable buckets (in the runtime's local time). skill "all" covers everything. */
export function bucketize(sessions: SessionPoint[], period: Period, skill: SkillKey | "all"): Bucket[] {
  const out: (Bucket & { rn: number; r: number })[] = [];
  let cur: (typeof out)[number] | null = null;
  sessions.forEach((s, i) => {
    const part = skill === "all" ? { n: s.n, c: s.c, r: s.r } : s.sk[skill] ? { n: s.sk[skill]![0], c: s.sk[skill]![1], r: s.sk[skill]![2] } : null;
    if (!part || !part.n) return;
    const key = keyFor(s.t, period, i);
    if (!cur || cur.key !== key) {
      cur = { key, t0: s.t, sessions: 0, n: 0, c: 0, points: 0, acc: null, pace: null, level: null, rn: 0, r: 0 };
      out.push(cur);
    }
    cur.sessions++; cur.n += part.n; cur.c += part.c; cur.r += part.r; cur.rn += part.n;
    if (skill === "all") cur.points += s.points;
    if (s.lv) {
      const vals = Object.values(s.lv) as number[];
      cur.level = skill === "all" ? vals.reduce((a, x) => a + x, 0) / vals.length : s.lv[skill] ?? cur.level;
    }
  });
  return out.slice(-PERIOD_LIMIT[period]).map(({ rn, r, ...b }) => ({
    ...b, acc: b.n ? (100 * b.c) / b.n : null, pace: rn ? (100 * r) / rn : null,
  }));
}

export function weekStart(d: Date): Date { return mondayOf(d); }
